import 'moment/locale/pt-br';

import React from 'react';
import config from '@plone/volto/registry';
import moment from 'moment';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';
import { Container as SemanticContainer } from 'semantic-ui-react';

import { getCompromissoTypeLabel } from '../Blocks/PersonAgenda/components/personAgendaMessages';
import {
  getCategoryStyle,
  normalizeCategoryToken,
} from '../Blocks/PersonAgenda/utils';

const messages = defineMessages({
  eventType: {
    id: 'compromissoView.eventType',
    defaultMessage: 'Event type',
  },
  date: {
    id: 'compromissoView.date',
    defaultMessage: 'Date',
  },
  time: {
    id: 'compromissoView.time',
    defaultMessage: 'Time',
  },
  location: {
    id: 'compromissoView.location',
    defaultMessage: 'Location',
  },
  requester: {
    id: 'compromissoView.requester',
    defaultMessage: 'Requester',
  },
  authority: {
    id: 'compromissoView.authority',
    defaultMessage: 'Authority',
  },
  attendees: {
    id: 'compromissoView.attendees',
    defaultMessage: 'Participants',
  },
});

const CompromissoView = (props) => {
  const { content } = props;
  const intl = useIntl();
  const locale = intl.locale || 'pt-br';
  moment.locale(locale);

  const Container =
    config.getComponent({ name: 'Container' }).component || SemanticContainer;

  const startDate = content?.start_date ? moment(content.start_date) : null;
  const endDate = content?.end_date ? moment(content.end_date) : null;

  // Corrigir timezone para exibir sempre em Brasília
  if (startDate) startDate.utcOffset('-03:00');
  if (endDate) endDate.utcOffset('-03:00');
  const typeToken = normalizeCategoryToken(
    content?.compromisso_type?.token || content?.compromisso_type || 'default',
  );
  const typeLabel = getCompromissoTypeLabel(
    typeToken,
    intl,
    content?.compromisso_type?.title || content?.compromisso_type_title || '',
  );
  const categoryStyle = getCategoryStyle(typeToken);

  const dateLabel =
    startDate && endDate
      ? `${startDate.format('DD/MM/YYYY')} - ${endDate.format('DD/MM/YYYY')}`
      : startDate
        ? startDate.format('DD/MM/YYYY')
        : '';

  const timeLabel =
    startDate && endDate
      ? `${startDate.format('HH:mm')} - ${endDate.format('HH:mm')}`
      : '';

  return (
    <Container id="page-document" className="view-wrapper">
      <h1 className="documentFirstHeading">{content?.title}</h1>

      {content?.description ? (
        <p className="documentDescription">{content.description}</p>
      ) : null}

      <div className="br-card mb-2x">
        <div className="card-content">
          <div className="row">
            {/* Badge de tipo: escolha entre pílula sólida ou retangular */}
            {typeLabel ? (
              <div className="col-12 col-md-6 mb-2x">
                <div className="text-up-01 text-gray-60">
                  {intl.formatMessage(messages.eventType)}
                </div>
                {/* Pílula sólida */}
                <span
                  style={{
                    display: 'inline-block',
                    padding: '0.25em 1.2em',
                    borderRadius: '999px',
                    fontWeight: 600,
                    backgroundColor: categoryStyle.backgroundColor || '#e5e5e5',
                    color: categoryStyle.textColor || '#222',
                  }}
                >
                  {typeLabel}
                </span>
                {/*
                // Se preferir retangular sem borda arredondada, troque borderRadius por 0 e ajuste o padding:
                <span
                  style={{
                    display: 'inline-block',
                    padding: '0.25em 1.2em',
                    borderRadius: 0,
                    fontWeight: 600,
                    backgroundColor:
                      categoryStyle.backgroundColor || '#e5e5e5',
                    color: categoryStyle.textColor || '#222',
                  }}
                >
                  {typeLabel}
                </span>
                */}
              </div>
            ) : null}

            {dateLabel ? (
              <div className="col-12 col-md-6 mb-2x">
                <div className="text-up-01 text-gray-60">
                  {intl.formatMessage(messages.date)}
                </div>
                <div className="text-weight-semi-bold">{dateLabel}</div>
              </div>
            ) : null}

            {timeLabel ? (
              <div className="col-12 col-md-6 mb-2x">
                <div className="text-up-01 text-gray-60">
                  {intl.formatMessage(messages.time)}
                </div>
                <div className="text-weight-semi-bold">{timeLabel}</div>
              </div>
            ) : null}

            {content?.location ? (
              <div className="col-12 col-md-6 mb-2x">
                <div className="text-up-01 text-gray-60">
                  {intl.formatMessage(messages.location)}
                </div>
                <div className="text-weight-semi-bold">{content.location}</div>
              </div>
            ) : null}

            {/* Autoridade */}
            {content?.autoridade ? (
              <div className="col-12 col-md-6 mb-2x">
                <div className="text-up-01 text-gray-60">
                  {intl.formatMessage(messages.authority)}
                </div>
                <div className="text-weight-semi-bold">
                  {content.autoridade}
                </div>
              </div>
            ) : null}

            {/* Solicitante */}
            {content?.solicitante ? (
              <div className="col-12 col-md-6 mb-2x">
                <div className="text-up-01 text-gray-60">
                  {intl.formatMessage(messages.requester)}
                </div>
                <div className="text-weight-semi-bold">
                  {content.solicitante}
                </div>
              </div>
            ) : null}
          </div>

          {/* Participantes (attendees) alinhados à esquerda */}
          {content?.attendees && (
            <div className="row">
              <div className="col-12 col-md-6 mb-2x">
                <div className="text-up-01 text-gray-60 mb-1">
                  {intl.formatMessage(messages.attendees)}
                </div>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {content.attendees
                    .split('\n')
                    .filter((a) => a.trim())
                    .map((name) => (
                      <li
                        key={name}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          marginBottom: 4,
                        }}
                      >
                        <i
                          className="fas fa-user"
                          aria-hidden="true"
                          style={{ marginRight: 8, color: '#555' }}
                        />
                        {name}
                      </li>
                    ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </Container>
  );
};

CompromissoView.propTypes = {
  content: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    location: PropTypes.string,
    solicitante: PropTypes.string,
    start_date: PropTypes.string,
    end_date: PropTypes.string,
    compromisso_type_title: PropTypes.string,
    compromisso_type: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        title: PropTypes.string,
        token: PropTypes.string,
      }),
    ]),
  }).isRequired,
};

export default CompromissoView;
